import { useEffect, useState } from 'react';
import $api from '@/http';
import { AvatarCache } from './AvatarCache';
import { subscribeAvatarSession } from './avatarSession';

/** Метаданные аватара элемента АК (зеркало backend AvatarMetaDto). */
export interface AvatarMeta {
    hasImage: boolean;
    photoVersion?: string | null;
}

const cache = new AvatarCache<AvatarMeta>(1000, 1000);
const pending = new Set<string>();
interface MetaRequest { controller: AbortController; keys: string[] }
const inFlight = new Map<string, MetaRequest>();
const listeners = new Map<string, Set<(meta: AvatarMeta | undefined) => void>>();
const invalidationListeners = new Set<(key?: string) => void>();
let scheduled = false;
const NO_IMAGE: AvatarMeta = { hasImage: false };

export function subscribeAvatarInvalidation(listener: (key?: string) => void): () => void {
    invalidationListeners.add(listener);
    return () => { invalidationListeners.delete(listener); };
}

function enqueue(key: string): void {
    if (inFlight.has(key)) return;
    pending.add(key);
    if (scheduled) return;
    scheduled = true;
    queueMicrotask(() => {
        scheduled = false;
        const keys = [...pending];
        pending.clear();
        // Сервер принимает не более 500 ключей в одном запросе.
        for (let offset = 0; offset < keys.length; offset += 500) {
            void flush(keys.slice(offset, offset + 500));
        }
    });
}

async function flush(keys: string[]): Promise<void> {
    const request: MetaRequest = { controller: new AbortController(), keys };
    keys.forEach((key) => inFlight.set(key, request));
    let items: Record<string, AvatarMeta> | null;
    try {
        const res = await $api.post('/addressBook/avatars/meta', { keys }, { suppressErrorNotification: true, signal: request.controller.signal, timeout: 15000 });
        items = (res.data as Record<string, AvatarMeta>) ?? {};
    } catch {
        items = null;
    }
    for (const key of keys) {
        // Ответ запроса, начатого до инвалидации, не должен возвращать старое фото.
        if (inFlight.get(key) !== request) continue;
        inFlight.delete(key);
        // Сбой фоновой проверки не означает, что фотография удалена.
        if (!items) continue;
        const meta = items[key] ?? NO_IMAGE;
        if (listeners.has(key)) cache.set(key, meta);
        listeners.get(key)?.forEach((cb) => cb(meta));
    }
}

/** Инвалидирует и кэш, и подписанные компоненты; активные ключи загружаются заново. */
export function invalidateAvatarMeta(userKey?: string): void {
    invalidationListeners.forEach((listener) => listener(userKey));
    const keys = userKey ? [userKey] : new Set([...cache.keys(), ...inFlight.keys(), ...listeners.keys()]);
    for (const key of keys) {
        cache.delete(key);
        cancelUnusedRequest(key);
        listeners.get(key)?.forEach((cb) => cb(undefined));
        if (listeners.get(key)?.size) enqueue(key);
    }
}

function cancelUnusedRequest(key: string): void {
    const request = inFlight.get(key);
    inFlight.delete(key);
    if (request && !request.keys.some((k) => inFlight.get(k) === request)) request.controller.abort();
}

const unsubscribeSession = subscribeAvatarSession((authenticated) => {
    pending.clear();
    for (const request of new Set(inFlight.values())) request.controller.abort();
    inFlight.clear();
    cache.clear();
    invalidationListeners.forEach((listener) => listener());
    listeners.forEach((subscribers, key) => {
        subscribers.forEach((cb) => cb(undefined));
        if (authenticated) enqueue(key);
    });
});
if (import.meta.hot) import.meta.hot.dispose(() => {
    unsubscribeSession();
    for (const request of new Set(inFlight.values())) request.controller.abort();
    inFlight.clear();
    pending.clear();
    listeners.clear();
    invalidationListeners.clear();
    cache.clear();
    window.removeEventListener('focus', refreshVisibleAvatars);
    document.removeEventListener('visibilitychange', refreshVisibleAvatars);
});

function refreshVisibleAvatars(): void {
    if (document.visibilityState !== 'visible') return;
    // Перепроверяем метаданные, сохраняя видимые фото и кэш их байтов.
    // Несмонтированные ключи тоже должны получить свежие метаданные при открытии.
    cache.clear();
    listeners.forEach((_subscribers, key) => enqueue(key));
}

/** Батчит запросы и сохраняет подписку после загрузки для последующей инвалидации. */
export function useAvatarMeta(userKey?: string): AvatarMeta | undefined {
    const [state, setState] = useState<{ key?: string; meta?: AvatarMeta }>(() => ({
        key: userKey,
        meta: userKey ? cache.get(userKey) : undefined,
    }));
    useEffect(() => {
        if (!userKey) return;
        if (listeners.size === 0) {
            window.addEventListener('focus', refreshVisibleAvatars);
            document.addEventListener('visibilitychange', refreshVisibleAvatars);
        }
        let subscribers = listeners.get(userKey);
        if (!subscribers) {
            subscribers = new Set();
            listeners.set(userKey, subscribers);
        }
        const cb = (meta: AvatarMeta | undefined): void => setState((previous) => {
            if (previous.key === userKey
                && previous.meta?.hasImage === meta?.hasImage
                && (previous.meta?.photoVersion ?? null) === (meta?.photoVersion ?? null)) return previous;
            return { key: userKey, meta };
        });
        subscribers.add(cb);
        const cached = cache.get(userKey);
        cb(cached);
        if (!cached) enqueue(userKey);
        return () => {
            subscribers.delete(cb);
            if (subscribers.size === 0) {
                listeners.delete(userKey);
                pending.delete(userKey);
                cancelUnusedRequest(userKey);
            }
            if (listeners.size === 0) {
                window.removeEventListener('focus', refreshVisibleAvatars);
                document.removeEventListener('visibilitychange', refreshVisibleAvatars);
            }
        };
    }, [userKey]);
    return state.key === userKey ? state.meta : undefined;
}
